import {
  ButtonClean,
  ButtonPrimary,
  Modal,
  Box,
  Switch,
  FormControl,
  Flex,
  Heading
} from 'front-end/src/components'
import { t, Trans } from '@lingui/macro'
import React, { useState } from 'react'
import { useManageEventContext } from 'front-end/src/pages/manage-events/ManageEventContext'
import { useFormikContext } from 'formik'
import { ManageEventDetailDayjs } from 'front-end/src/pages/manage-events/event-edit/EventEditPage'
import { RecurringEventAttendanceTypeEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { SenderSelect } from 'front-end/src/pages/manage-campaign-page/campaign-new-action/SenderSelect'
import { brandColors } from 'shared/basicPalette'
import IconExclamation from 'front-end/src/components/common/icon/IconExclamation'
import Message, {
  MessageTypes
} from 'front-end/src/components/common/message/Message'
import { EventUsedInCampaignsWarning } from 'front-end/src/pages/manage-events/EventUsedInCampaignsWarning'

export const PublishConfirmModal = ({ resolve, reject, relatedCampaigns }) => {
  const { event, isRecurringInstance } = useManageEventContext()
  const { values, touched, dirty } = useFormikContext<ManageEventDetailDayjs>()
  const [emailIdentityId, setEmailIdentityId] = useState<number | null>(null)
  const [notifyInvitees, setNotifyInvitees] = useState(false)
  const [notifyRSVPdInvitees, setNotifyRSVPdInvitees] = useState(false)
  const isRecurringSeries =
    (!!event?.recurrence?.freq || !!values.recurringEvent?.recurrence?.freq) &&
    !isRecurringInstance

  const getPublishConfirmText = () => {
    if (isRecurringSeries && event.published_date) {
      return (
        <Heading size="md" lineHeight="24px">
          <Trans>You're about to update event series</Trans>
        </Heading>
      )
    }

    if (isRecurringSeries) {
      return (
        <Trans>
          Are you sure you want to <strong>publish this event series?</strong>
        </Trans>
      )
    }

    if (isRecurringInstance && event.published_date) {
      return <Trans>You're about to update an event occurrence.</Trans>
    }

    if (event.published_date) {
      return (
        <Trans>
          You're about to <strong>update the event</strong>.
        </Trans>
      )
    }

    return (
      <Trans>
        Are you sure you want to <strong>publish this event?</strong>
      </Trans>
    )
  }

  const getWarningText = () => {
    const isUpdateRecurrence = isRecurringSeries && event.published_date
    const isTimeChanged = touched.start_time || touched.end_time

    if (isUpdateRecurrence && isTimeChanged) {
      return (
        <Trans>
          All future occurrences in this event series will be recreated, changes
          will be deleted, RSVPs will be cleared because time was changed, but
          invitees will remain.
        </Trans>
      )
    }

    if (isUpdateRecurrence && dirty) {
      return (
        <Trans>
          All future occurrences in this event series will be recreated, changes
          will be deleted, but invitees and RSVPs will remain.
        </Trans>
      )
    }

    if (
      !isRecurringSeries &&
      event.published_date &&
      (touched.start_time || touched.end_time)
    ) {
      return (
        <Trans>
          You are changing the date or time of the event, RSVPs will be cleared.
        </Trans>
      )
    }

    return ''
  }

  const publishButtonText = () => {
    if (event.published_date) {
      return <Trans>Publish changes</Trans>
    }
    if (isRecurringSeries) {
      return <Trans>Publish series</Trans>
    }
    return <Trans>Publish event</Trans>
  }

  const warningText = getWarningText()

  return (
    <Modal
      newDesign
      controls={
        <>
          <ButtonClean onClick={reject}>
            <Trans>Cancel</Trans>
          </ButtonClean>
          <ButtonPrimary
            onClick={() =>
              resolve({
                notifyInvitees,
                notifyRSVPdInvitees,
                emailIdentityId
              })
            }
          >
            {publishButtonText()}
          </ButtonPrimary>
        </>
      }
      onHide={reject}
    >
      <Box fontSize="18px" mt="48px" mb="16px">
        {getPublishConfirmText()}
      </Box>

      {warningText && (
        <Message type={MessageTypes.ERROR} mb="24px">
          <IconExclamation mr="8px" />
          <Box>{warningText}</Box>
        </Message>
      )}

      <EventUsedInCampaignsWarning relatedCampaigns={relatedCampaigns} />

      {isRecurringSeries && !event.published_date && (
        <Box mb="16px">
          <Trans>
            The Type of attendance is set to the{' '}
            <strong>
              {values.attendance_type === RecurringEventAttendanceTypeEnum.all
                ? t`Attend multiple occurrences`
                : t`Attend single occurrence`}
            </strong>
            . This can not be changed later.
          </Trans>
        </Box>
      )}

      {event.inviteesCount > 0 && (
        <>
          <Box
            width="100%"
            height="1px"
            bgColor={brandColors.greyTransparent[7]}
            mb="16px"
            mt="24px"
          />
          <FormControl
            as={Flex}
            alignItems="center"
            maxW="60%"
            justifyContent="space-between"
            mb="8px"
          >
            <Trans>Notify invitees via email</Trans>
            <Switch
              isChecked={notifyInvitees}
              onChange={(e) => {
                setNotifyInvitees(e.target.checked)
                setNotifyRSVPdInvitees(false)
              }}
              dataTest="EmailInviteesSwitch"
            />
          </FormControl>

          <FormControl
            as={Flex}
            alignItems="center"
            maxW="60%"
            justifyContent="space-between"
            mt="8px"
          >
            <Trans>Only those who accepted invitation</Trans>
            <Switch
              isChecked={notifyRSVPdInvitees}
              onChange={(e) => {
                setNotifyRSVPdInvitees(e.target.checked)
                setNotifyInvitees(false)
              }}
              dataTest="EmailRSVPdInviteesSwitch"
            />
          </FormControl>

          {(notifyInvitees || notifyRSVPdInvitees) && (
            <SenderSelect
              value={emailIdentityId}
              onChange={(id) => setEmailIdentityId(id)}
            />
          )}
        </>
      )}
    </Modal>
  )
}
